import { Card } from "@excalidraw/excalidraw/components/Card";
import { IconButton } from "@excalidraw/excalidraw/components/IconButton";
import {
  copyIcon,
  ExportImageIcon,
  LinkIcon,
} from "@excalidraw/excalidraw/components/icons";
import { useState } from "react";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { useAtomValue } from "../app-jotai";
import { getDiagramMuidFromUrl } from "../data";
import { meAtom } from "../data/me";
import {
  saveSceneToWorkspace,
  signInThenSaveToWorkspace,
} from "../data/promote";

// The app-specific cards for the Save ("Save to…") dialog, rendered through the
// editor's `renderCustomUI` export hook alongside the built-in "Save to disk"
// card:
//   • Export as image — opens the image export dialog (relocated here from the
//     Open dialog, where it was confusing).
//   • Shareable link — opens our per-diagram ShareDialog (the same flow as the
//     menu's "Share link"), replacing upstream's legacy JSON-blob backend link.
//   • Save to workspace / Save a copy — promotes the scene into a server diagram
//     (context-aware label). Anonymous users are routed through sign-in first,
//     then the save resumes automatically on return.
export const ExportDialogExtras: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI;
  onShareLink: () => void;
}> = ({ excalidrawAPI, onShareLink }) => {
  const [busy, setBusy] = useState(false);
  const me = useAtomValue(meAtom);

  // On the local canvas this is a "promote to server"; on an existing server
  // diagram it's a fork/duplicate.
  const onServerDiagram = !!getDiagramMuidFromUrl();
  const saveLabel = onServerDiagram ? "Save a copy" : "Save to workspace";
  const saveDetails = onServerDiagram
    ? "Fork this scene into a new diagram in your workspace."
    : "Save this drawing to your workspace so you can reopen it anywhere.";

  const exportImage = () => {
    excalidrawAPI.updateScene({
      appState: { openDialog: { name: "imageExport" } },
    });
  };

  const shareLink = () => {
    excalidrawAPI.updateScene({ appState: { openDialog: null } });
    onShareLink();
  };

  const saveToWorkspace = async () => {
    if (busy) {
      return;
    }
    // Anonymous → sign in first; the save resumes on return (App resume effect).
    if (!me?.user) {
      signInThenSaveToWorkspace();
      return;
    }
    setBusy(true);
    try {
      const baseName = excalidrawAPI.getName() || "Untitled";
      await saveSceneToWorkspace(
        excalidrawAPI,
        onServerDiagram ? `${baseName} copy` : baseName,
      );
      // saveSceneToWorkspace navigates on success; nothing else to do here.
    } catch (error: any) {
      setBusy(false);
      excalidrawAPI.updateScene({
        appState: { errorMessage: error.message },
      });
    }
  };

  // Anonymous users see the sign-in hint on the action itself.
  const saveButtonLabel = me?.user ? saveLabel : `${saveLabel} (sign in)`;

  return (
    <>
      <Card color="primary">
        <div className="Card-icon">{ExportImageIcon}</div>
        <h2>Export as image</h2>
        <div className="Card-details">
          Export the scene as a PNG or SVG image.
        </div>
        <IconButton
          className="Card-button"
          type="button"
          title="Export as image"
          aria-label="Export as image"
          showAriaLabel={true}
          onClick={exportImage}
        />
      </Card>
      <Card color="pink">
        <div className="Card-icon">{LinkIcon}</div>
        <h2>Shareable link</h2>
        <div className="Card-details">
          Turn on a public link to this diagram — view-only or editable.
        </div>
        <IconButton
          className="Card-button"
          type="button"
          title="Share link"
          aria-label="Share link"
          showAriaLabel={true}
          onClick={shareLink}
        />
      </Card>
      <Card color="primary">
        <div className="Card-icon">{copyIcon}</div>
        <h2>{saveLabel}</h2>
        <div className="Card-details">{saveDetails}</div>
        <IconButton
          className="Card-button"
          type="button"
          title={saveButtonLabel}
          aria-label={saveButtonLabel}
          showAriaLabel={true}
          isLoading={busy}
          onClick={saveToWorkspace}
        />
      </Card>
    </>
  );
};
